import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { recordHomeHit } from '../utils/pageHits';

const SLIDES = [
  {
    cls: 'slide-1',
    eyebrow: 'Tech Learning Platform',
    eyebrowStyle: {},
    title: <>Master <span className="hl-blue">Cloud &amp; Code</span><br />with Expert Guidance</>,
    desc: 'Hands-on AWS training, programming mastery, practice exams, and 1-on-1 mentorship — all in one place.',
    btn1: { to: '/programs', label: 'Explore Programs', cls: 'btn-blue' },
    btn2: { to: '/practice-exam', label: 'Try Free Exam', cls: 'btn-outline-blue' },
  },
  {
    cls: 'slide-2',
    eyebrow: '1-on-1 Expert Mentorship',
    eyebrowStyle: { borderColor: 'var(--orange-light)', color: 'var(--orange)' },
    title: <>Learn at <span className="hl-orange">Your Pace</span><br />With a Dedicated Mentor</>,
    desc: 'Personalized sessions for AWS, Python, Java, .NET, C/C++, Data Structures & System Design — tailored to your goals.',
    btn1: { to: '/request?tab=session', label: 'Book a Session', cls: 'btn-orange' },
    btn2: { to: '/training', label: 'View Training', cls: 'btn-outline-blue' },
  },
  {
    cls: 'slide-3',
    eyebrow: 'AWS Certification Prep',
    eyebrowStyle: { borderColor: 'var(--green)', color: '#16a34a' },
    title: <>Crack <span className="hl-blue">AWS Certification</span><br />on Your <span className="hl-orange">First Attempt</span></>,
    desc: '200+ practice questions with detailed explanations. Real exam simulations for CCP, SAA, DVA and more.',
    btn1: { to: '/practice-exam', label: 'Start Free Exam', cls: 'btn-blue' },
    btn2: { to: '/request?tab=access', label: 'Request Full Access', cls: 'btn-outline-blue' },
  },
];

const TESTIMONIALS = [
  { quote: "360DigiLearning's AWS course was exactly what I needed. The 1-on-1 sessions helped me crack my SAA-C03 on the first attempt. My instructor was incredibly patient and knowledgeable.", author: 'Rahul Mehta', role: 'AWS Solutions Architect' },
  { quote: 'The DSA training helped me land my dream job at top MNC. The problem-solving approach is systematic and genuinely effective. I highly recommend the 1-on-1 sessions.', author: 'Priya Sharma', role: 'Software Engineer' },
  { quote: 'The practice exam bank is outstanding! Questions are very close to the real AWS exam. The detailed answer explanations helped me understand underlying concepts deeply.', author: 'Arun Kumar', role: 'Cloud Architect' },
  { quote: 'I went from zero Python knowledge to building cloud automation scripts in 6 weeks. The personalized training sessions are completely worth every rupee spent.', author: 'Sneha Iyer', role: 'DevOps Engineer' },
];

const PROGRAMS = [
  { to: '/programs', icon: '☁️', title: 'AWS Cloud Solutions', desc: 'EC2, S3, Lambda, VPC, RDS, IAM. Prep for CCP, SAA, DVA certifications.', tag: 'Beginner → Advanced', tagCls: '' },
  { to: '/programs', icon: '⚙️', title: 'System Design', desc: 'Scalable, fault-tolerant systems. Microservices, load balancing, caching.', tag: 'Architecture', tagCls: 'orange', iconCls: 'orange' },
  { to: '/programs', icon: '💻', title: 'Programming Languages', desc: 'Python, Java, .NET, C, C++ — with real-world projects and exercises.', tag: '5 Languages', tagCls: '' },
  { to: '/programs', icon: '🧩', title: 'Problem Solving & DSA', desc: 'Data structures and algorithms for interviews and competitive programming.', tag: 'Interview Ready', tagCls: 'orange', iconCls: 'orange' },
  { to: '/practice-exam', icon: '📝', title: 'Practice Exams', desc: '10 free sample questions per category. Unlock 200+ with access key.', tag: 'Free + Full Access', tagCls: '' },
  { to: '/training', icon: '🎯', title: '1-on-1 Training', desc: 'Live personal sessions with certified instructors. Tailored curriculum.', tag: 'Personal Mentorship', tagCls: 'orange', iconCls: 'orange' },
  { to: '/programs', icon: '🏗️', title: 'Cloud Architecture', desc: 'Well-Architected Framework, IaC, DevOps, cost optimization.', tag: 'Enterprise Grade', tagCls: '' },
  { to: '/programs', icon: '🤖', title: 'AI & Machine Learning', desc: 'ML fundamentals, deep learning, neural networks, GenAI & LLMs with hands-on projects.', tag: 'Beginner → Expert', tagCls: 'orange', iconCls: 'orange' },
  { to: '/programs', icon: '🔬', title: 'Solution Architect Path', desc: 'End-to-end architect training covering design principles and cloud patterns.', tag: 'Career Path', tagCls: 'orange', iconCls: 'orange' },
];

const HomePage: React.FC = () => {
  useEffect(() => { recordHomeHit(); }, []);
  const [slide, setSlide] = useState(0);
  const [tSlide, setTSlide] = useState(0);
  const [bannerVisible, setBannerVisible] = useState(true);
  const slideTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const tTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const goTo = useCallback((n: number) => {
    setSlide((n + SLIDES.length) % SLIDES.length);
  }, []);

  const resetSlideTimer = useCallback(() => {
    if (slideTimer.current) clearInterval(slideTimer.current);
    slideTimer.current = setInterval(() => setSlide(s => (s + 1) % SLIDES.length), 5200);
  }, []);

  useEffect(() => {
    resetSlideTimer();
    return () => { if (slideTimer.current) clearInterval(slideTimer.current); };
  }, [resetSlideTimer]);

  useEffect(() => {
    tTimer.current = setInterval(() => setTSlide(s => (s + 1) % TESTIMONIALS.length), 4500);
    return () => { if (tTimer.current) clearInterval(tTimer.current); };
  }, []);

  const handlePrev = () => { goTo(slide - 1); resetSlideTimer(); };
  const handleNext = () => { goTo(slide + 1); resetSlideTimer(); };
  const handleDot = (i: number) => { goTo(i); resetSlideTimer(); };

  return (
    <Layout>
      {/* PROMO BANNER */}
      {bannerVisible && (
        <div className="promo-banner">
          <p>🎉 <strong>Limited Time:</strong> Get 3 months FREE exam access with any training session booking!</p>
          <Link to="/request?tab=session" className="pb-cta">Claim Offer →</Link>
          <button className="pb-close" onClick={() => setBannerVisible(false)} title="Close">✕</button>
        </div>
      )}

      {/* HERO SLIDER */}
      <div className="hero-slider" style={{ marginTop: 0 }}>
        {SLIDES.map((s, i) => (
          <div key={i} className={`slide ${s.cls}${slide === i ? ' active' : ''}`}>
            <div className="slide-content">
              <div className="slide-eyebrow" style={s.eyebrowStyle}>
                <span className="pip" style={s.eyebrowStyle?.color ? { background: 'var(--blue)' } : {}} />
                {s.eyebrow}
              </div>
              <h1 className="slide-h1">{s.title}</h1>
              <p className="slide-p">{s.desc}</p>
              <div className="slide-btns">
                <Link to={s.btn1.to} className={`btn btn-lg ${s.btn1.cls}`}>{s.btn1.label}</Link>
                <Link to={s.btn2.to} className={`btn btn-lg ${s.btn2.cls}`}>{s.btn2.label}</Link>
              </div>
            </div>
          </div>
        ))}
        <button className="slider-prev" onClick={handlePrev}>←</button>
        <button className="slider-next" onClick={handleNext}>→</button>
        <div className="slider-dots">
          {SLIDES.map((_, i) => (
            <button key={i} className={`sdot${slide === i ? ' on' : ''}`} onClick={() => handleDot(i)} />
          ))}
        </div>
      </div>

      {/* STATS BAR */}
      <div className="stats-bar">
        {[
          { num: '5', sup: 'K+', label: 'Learners Enrolled' },
          { num: '200', sup: '+', label: 'Practice Questions' },
          { num: '15', sup: '+', label: 'Expert Instructors' },
          { num: '98', sup: '%', label: 'Satisfaction Rate' },
          { num: '24', sup: '/7', label: 'Learning Access' },
        ].map((s, i) => (
          <div className="stat-cell" key={i}>
            <div className="stat-num">{s.num}<em>{s.sup}</em></div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* PROGRAMS OVERVIEW */}
      <div className="section">
        <div className="section-center">
          <span className="section-tag">What We Offer</span>
          <h2 className="section-h2">Comprehensive Learning Paths</h2>
          <p className="section-sub">Everything you need to go from beginner to professional in the most in-demand tech skills.</p>
        </div>
        <div className="cards-grid container">
          {PROGRAMS.map((p, i) => (
            <Link to={p.to} className="card" key={i} style={{ textDecoration: 'none' }}>
              <div className={`card-icon${p.iconCls ? ' ' + p.iconCls : ''}`}>{p.icon}</div>
              <h3>{p.title}</h3>
              <p>{p.desc}</p>
              <span className={`card-tag${p.tagCls ? ' ' + p.tagCls : ''}`}>{p.tag}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* WHY US BANNER */}
      <div style={{ background: 'linear-gradient(135deg,var(--blue-pale),var(--orange-pale))', padding: '60px 6%', borderTop: '1px solid var(--gray-200)', borderBottom: '1px solid var(--gray-200)' }}>
        <div className="container" style={{ display: 'flex', alignItems: 'center', gap: '48px', flexWrap: 'wrap', justifyContent: 'center' }}>
          <div style={{ maxWidth: '440px' }}>
            <span className="section-tag orange">Why 360DigiLearning?</span>
            <h2 className="section-h2" style={{ marginTop: '8px' }}>Learn from Active<br />Industry Experts</h2>
            <p style={{ color: 'var(--gray-600)', lineHeight: 1.75, marginTop: '11px', fontSize: '0.97rem' }}>All instructors are working engineers with 10+ years of industry experience. Real problems, real solutions, real results.</p>
            <div style={{ display: 'flex', gap: '11px', flexWrap: 'wrap', marginTop: '22px' }}>
              <Link to="/training" className="btn btn-blue">View Instructors</Link>
              <Link to="/request" className="btn btn-outline-blue">Book Free Call</Link>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '13px', flexShrink: 0 }}>
            {[
              { icon: '🏆', title: 'Certified Experts', sub: 'AWS & Azure certified' },
              { icon: '⚡', title: 'Live Sessions', sub: 'Real-time learning' },
              { icon: '📊', title: 'Progress Tracking', sub: 'Measure your growth' },
              { icon: '💬', title: '24/7 Support', sub: 'Always here to help' },
            ].map((item, i) => (
              <div key={i} style={{ background: '#fff', borderRadius: '14px', padding: '18px', textAlign: 'center', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--gray-200)' }}>
                <div style={{ fontSize: '1.7rem', marginBottom: '5px' }}>{item.icon}</div>
                <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--gray-800)' }}>{item.title}</div>
                <div style={{ color: 'var(--gray-400)', fontSize: '0.78rem', marginTop: '2px' }}>{item.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* TESTIMONIALS */}
      <div className="section" style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
        <div className="section-center" style={{ marginBottom: '32px' }}>
          <span className="section-tag">Testimonials</span>
          <h2 className="section-h2" style={{ marginTop: '8px' }}>What Our Learners Say</h2>
        </div>
        <div className="tslider-wrap">
          <div className="tslider-track" style={{ transform: `translateX(-${tSlide * 100}%)` }}>
            {TESTIMONIALS.map((t, i) => (
              <div className="tslide" key={i}>
                <div className="tcard">
                  <div className="tcard-quote">"</div>
                  <p>{t.quote}</p>
                  <div className="tcard-author">{t.author}</div>
                  <div className="tcard-role">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="t-dots">
          {TESTIMONIALS.map((_, i) => (
            <button key={i} className={`tdot${tSlide === i ? ' on' : ''}`} onClick={() => setTSlide(i)} />
          ))}
        </div>
      </div>

      {/* CTA */}
      <div style={{ background: 'var(--blue-pale)', padding: '68px 6%', textAlign: 'center', borderTop: '1px solid var(--blue-soft)' }}>
        <span className="section-tag">Start Today</span>
        <h2 className="section-h2" style={{ marginTop: '8px' }}>Ready to Elevate Your Career?</h2>
        <p className="section-sub">Join thousands of engineers who've transformed their careers with 360DigiLearning.</p>
        <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '20px' }}>
          <Link to="/request" className="btn btn-orange btn-lg">Request a Session</Link>
          <Link to="/practice-exam" className="btn btn-outline-blue btn-lg">Try Free Exam</Link>
        </div>
      </div>
    </Layout>
  );
};

export default HomePage;
